import { useTranslation } from "react-i18next"

import type { SkillPackageBudget, SkillPackageFormErrorCode } from "../utils/skill-package-draft"

import { SKILL_PACKAGE_FORM_ERROR_DEFAULTS } from "../utils/skill-package-editor-constants"

// The two small things every tab of the skill-package editor needs (#3074):
// a budget counter and a validation message.
//
// They are shared rather than copied because the whole point of the view is
// that a cap is visible while you type instead of discovered as a 400 — and a
// counter that renders bytes in one tab and characters in another would make
// the numbers untrustworthy exactly where trust is the feature.

export function SkillPackageBudgetCounter({
  budget,
  unit,
}: {
  budget: SkillPackageBudget
  unit: "bytes" | "files" | "cases"
}) {
  const { t } = useTranslation()
  // Bytes are shown in kB because the caps are quoted in kB; below 10 kB one
  // decimal keeps "0.4 kB of 64 kB" from reading as an empty field.
  const format = (value: number) =>
    unit === "bytes" ? `${(value / 1024).toFixed(value < 10240 ? 1 : 0)} kB` : String(value)
  return (
    <span
      data-testid="skill-package-budget"
      className={
        budget.over
          ? "bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
          : "text-muted-foreground text-xs"
      }
    >
      {t("adminDashboard.skillPackages.editor.budget", {
        defaultValue: "{{used}} of {{max}}",
        used: format(budget.used),
        max: format(budget.max),
      })}
    </span>
  )
}

export function SkillPackageFormError({
  codes,
  match,
  show,
}: {
  codes: SkillPackageFormErrorCode[]
  match: SkillPackageFormErrorCode[]
  show: boolean
}) {
  const { t } = useTranslation()
  if (!show) return null
  const code = codes.find((candidate) => match.includes(candidate))
  if (!code) return null
  return (
    <p
      className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
      role="alert"
    >
      {t(`adminDashboard.skillPackages.editor.errors.${code}`, {
        defaultValue: SKILL_PACKAGE_FORM_ERROR_DEFAULTS[code],
      })}
    </p>
  )
}
